import { useState, useRef, useEffect, useMemo, type ImgHTMLAttributes } from "react";

/*
 * LazyImage — Lazy-loaded image with blur-up placeholder effect
 * Design: Matches ASPAB light theme with subtle gray placeholder
 *
 * Features:
 * - IntersectionObserver-based lazy loading (loads when near viewport)
 * - Smooth blur-to-sharp transition on load
 * - Low-contrast placeholder background while loading
 * - Supports all standard img attributes (className, alt, etc.)
 * - Priority prop to skip lazy loading for above-the-fold images
 * - Responsive srcset generation for CDN-hosted images
 */

/** Breakpoint widths for responsive images */
const RESPONSIVE_WIDTHS = [320, 640, 768, 1024, 1280, 1920];

/**
 * Generate srcset string from a base image URL.
 * Works with CDN URLs that support width parameters via query string.
 * For non-CDN URLs, returns undefined (browser uses src as-is).
 */
function generateSrcSet(src: string | undefined): string | undefined {
  if (!src) return undefined;
  // Only generate srcset for locally hosted images (WebP/JPG/PNG)
  const isCdnImage =
    src.startsWith("/images/") &&
    /\.(webp|jpg|jpeg|png)$/i.test(src);
  if (!isCdnImage) return undefined;

  // Use width descriptor srcset — browser picks the best size
  return RESPONSIVE_WIDTHS.map((w) => `${src} ${w}w`).join(", ");
}

/** Default sizes attribute for responsive images */
const DEFAULT_SIZES = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";

interface LazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Skip lazy loading — use for hero/above-the-fold images */
  priority?: boolean;
  /** Additional wrapper className */
  wrapperClassName?: string;
  /** Custom sizes attribute for responsive images. Defaults to a sensible breakpoint set. */
  sizes?: string;
}

export default function LazyImage({
  src,
  alt = "",
  className = "",
  priority = false,
  wrapperClassName = "",
  sizes,
  ...rest
}: LazyImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLDivElement>(null);

  const srcSet = useMemo(() => generateSrcSet(src), [src]);
  const imgSizes = sizes || (srcSet ? DEFAULT_SIZES : undefined);

  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }

    const el = imgRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(el);
        }
      },
      {
        rootMargin: "200px 0px", // Start loading 200px before entering viewport
        threshold: 0,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [priority]);

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden ${wrapperClassName}`}
    >
      {/* Placeholder background */}
      <div
        className={`absolute inset-0 bg-gray-200 transition-opacity duration-500 ${
          isLoaded ? "opacity-0" : "opacity-100"
        }`}
        aria-hidden="true"
      >
        {/* Subtle shimmer animation while loading */}
        {!isLoaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
        )}
      </div>

      {/* Actual image */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          srcSet={srcSet}
          sizes={imgSizes}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          fetchPriority={priority ? "high" : "auto"}
          className={`transition-all duration-700 ${
            isLoaded ? "opacity-100 blur-0 scale-100" : "opacity-0 blur-sm scale-[1.02]"
          } ${className}`}
          onLoad={() => setIsLoaded(true)}
          {...rest}
        />
      )}

      {/* Fallback for when not in view — maintains layout space */}
      {!isInView && (
        <div className={className} style={{ aspectRatio: rest.width && rest.height ? `${rest.width}/${rest.height}` : undefined }} />
      )}
    </div>
  );
}
